/* ----------------------------------------------------------------------------
   Public content store.

   The public site is prerendered, so the content a visitor first sees is baked
   into the HTML at build time. This module is how that content gets in and out:

     1. `scripts/prerender.mjs` fetches published content from the API and calls
        setContentSnapshot() before rendering each route, then writes the same
        snapshot into the document as a <script type="application/json"> tag.
     2. In the browser, the snapshot is read straight back out of that tag, so
        hydration sees byte-identical data and never mismatches.
     3. After hydration, revalidate() refreshes from the API in the background.
        That is what makes maintenance mode flip in seconds rather than waiting
        on a rebuild.

   Read-only by design. Editing goes through the dashboard, which talks to the
   API directly — see src/admin/store.ts.
---------------------------------------------------------------------------- */
import { useSyncExternalStore } from "react"
import type { Collections, DB, Settings } from "./seed"

/*
  Types only. A value re-export here would keep SEED reachable from the public
  entry, and rolldown would then have to retain the whole seed — which is the
  thing fallback() goes out of its way to drop. Formatters are plain functions
  and cost nothing, so they pass through as values.
*/
export type * from "./seed"
export * from "./format"

/** The id the prerenderer writes and the browser reads. */
export const SNAPSHOT_ELEMENT_ID = "__sb_content"

/** The subset of the store the public site actually renders. */
export type ContentSnapshot = {
  caseStudies: DB["caseStudies"]
  posts: DB["posts"]
  settings: Settings
}

const API_URL = (import.meta.env?.VITE_API_URL ?? "").replace(/\/$/, "")

/*
  Settings the site falls back to before anything has loaded. Spelled out here
  rather than taken from SEED so that the production bundle has no reason to
  reference the seed at all — see fallback() below.
*/
const DEFAULT_SETTINGS: Settings = {
  maintenance: false,
  maintenanceTitle: "We're making things better",
  maintenanceMessage:
    "Sableboxx is undergoing scheduled maintenance. We'll be back online shortly. Thank you for your patience.",
  maintenanceEta: "",
}

const EMPTY: ContentSnapshot = {
  caseStudies: [],
  posts: [],
  settings: DEFAULT_SETTINGS,
}

/**
 * What the store holds before real content arrives.
 *
 * Empty, and this module imports nothing but types from ./seed on purpose.
 * Any value import would put the seed in the public graph, and rolldown would
 * hoist it into the shared chunk every visitor downloads — where it would sit
 * as several kilobytes of invented client names and case studies. Shipping
 * those alongside the real work is not a size problem, it is a credibility
 * one.
 *
 * Production never renders this: every prerendered page carries an embedded
 * snapshot, so the fallback only covers the instant before it is read. In
 * development entry-client.tsx loads the seed dynamically so `pnpm dev` still
 * shows a populated site with no API running.
 */
function fallback(): ContentSnapshot {
  return EMPTY
}

/**
 * Normalises whatever the API or the embedded snapshot gave us. A malformed or
 * half-written payload must never be able to blank the site, so every field
 * falls back to the seed rather than to undefined.
 */
function normalise(input: unknown): ContentSnapshot {
  const seed = fallback()
  if (!input || typeof input !== "object") return seed
  const raw = input as Partial<ContentSnapshot>
  return {
    caseStudies: Array.isArray(raw.caseStudies) ? raw.caseStudies : seed.caseStudies,
    posts: Array.isArray(raw.posts) ? raw.posts : seed.posts,
    settings: { ...seed.settings, ...(raw.settings ?? {}) },
  }
}

function readEmbedded(): ContentSnapshot | null {
  if (typeof document === "undefined") return null
  const el = document.getElementById(SNAPSHOT_ELEMENT_ID)
  if (!el?.textContent) return null
  try {
    return normalise(JSON.parse(el.textContent))
  } catch {
    return null
  }
}

let snapshot: ContentSnapshot = readEmbedded() ?? fallback()

const listeners = new Set<() => void>()

/** Called by the prerenderer before rendering, and by revalidate() at runtime. */
export function setContentSnapshot(next: unknown) {
  snapshot = normalise(next)
  listeners.forEach((l) => l())
}

export function getContentSnapshot(): ContentSnapshot {
  return snapshot
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/* --------------------------------------------------------------------------
   Hooks. These keep the signatures the public pages already use, so Work,
   Insights, Home and Root did not have to change shape when the data moved
   from localStorage to a real API.
-------------------------------------------------------------------------- */
export function useCollection<K extends "caseStudies" | "posts">(
  name: K,
): { items: Collections[K] } {
  const items = useSyncExternalStore(
    subscribe,
    () => snapshot[name] as Collections[K],
    () => snapshot[name] as Collections[K],
  )
  return { items }
}

export function useSettings(): { settings: Settings } {
  const settings = useSyncExternalStore(
    subscribe,
    () => snapshot.settings,
    () => snapshot.settings,
  )
  return { settings }
}

/* --------------------------------------------------------------------------
   Runtime revalidation
-------------------------------------------------------------------------- */
let revalidated = false

/**
 * Refreshes content from the API once per page load, after hydration.
 *
 * Deliberately silent on failure: the prerendered snapshot is already correct
 * as of the last build, so an unreachable API should leave the site exactly as
 * it is rather than surface an error to a visitor who has no use for it.
 */
export async function revalidateContent(): Promise<void> {
  if (!API_URL || revalidated || typeof window === "undefined") return
  revalidated = true

  try {
    const res = await fetch(`${API_URL}/api/public/content`, {
      headers: { accept: "application/json" },
    })
    if (!res.ok) return
    setContentSnapshot(await res.json())
  } catch {
    /* offline, CORS, API asleep — keep the build-time content */
  }
}

/** Serialises the snapshot for embedding in prerendered HTML. */
export function serialiseSnapshot(data: ContentSnapshot): string {
  /*
    Content is authored in the dashboard, so it is trusted-ish — but it lands
    inside a <script> element, where a literal "</script>" in a case study body
    would end the tag early and drop the rest of the page. Escaping the angle
    brackets and the U+2028/2029 line separators makes that impossible.
  */
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029")
}
